import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  SummaryCard,
  TemplateGroup,
  AddTemplateDialog,
  FieldExtractionDialog,
} from './sub-modules/ShareManagement';
import { apiService } from '../../../services/api';
import { PdfTemplate } from '../../../types';


const ShareManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [templates, setTemplates] = useState<PdfTemplate[]>([]);
  const [unmappedDocuments, setUnmappedDocuments] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [extractingFields, setExtractingFields] = useState(false);
  const [extractedFields, setExtractedFields] = useState<string[]>([]);
  const [currentTemplateId, setCurrentTemplateId] = useState<string>('');
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [fieldExtractionDialogOpen, setFieldExtractionDialogOpen] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Group templates by type
  const groupedTemplates = useMemo(() => {
    const groups: Record<string, PdfTemplate[]> = {};
    
    templates.forEach(template => {
      if (!groups[template.templateType]) {
        groups[template.templateType] = [];
      }
      groups[template.templateType].push(template);
    });
    
    return groups;
  }, [templates]);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all documents
      const allDocuments = await apiService.getDocuments();
      setDocuments(allDocuments);
      
      // Load all PDF templates
      const allTemplates = await apiService.getPdfTemplates();
      setTemplates(allTemplates);
      
      // Load unmapped documents
      const unmappedDocs = await apiService.getUnmappedDocuments();
      setUnmappedDocuments(unmappedDocs);
      
      // Load permissions for template types
      const permissionsData = await apiService.getPermissions();
      setPermissions(permissionsData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Show snackbar
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Extract fields from PDF
  const extractFields = useCallback(async (documentFileId: string, templateId?: string) => {
    try {
      setExtractingFields(true);
      setExtractedFields([]);
      setCurrentTemplateId(templateId || documentFileId);
      
      const result = await apiService.extractPdfFields(documentFileId);
      
      if (result.hasFields) {
        setExtractedFields(result.fields);
        showSnackbar(`Found ${result.fields.length} mappable fields`);
      } else {
        showSnackbar('No mappable fields found in this document', 'error');
      }
      
      setFieldExtractionDialogOpen(true);
    } catch (err) {
      console.error('Error extracting fields:', err);
      showSnackbar('Failed to extract fields from document', 'error');
    } finally {
      setExtractingFields(false);
    }
  }, [showSnackbar]);

  // Save template
  const saveTemplate = useCallback(async () => {
    if (!selectedDocumentId || !selectedTemplateType) {
      showSnackbar('Please select a document and template type', 'error');
      return;
    }

    try {
      setSaving(true);
      
      await apiService.setPdfTemplate({
        templateType: selectedTemplateType,
        documentFileId: selectedDocumentId
      });
      
      showSnackbar('Template saved successfully');
      loadData(); // Reload data to get the updated template
      setAddDialogOpen(false);
    } catch (err) {
      console.error('Error saving template:', err);
      showSnackbar('Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedDocumentId, selectedTemplateType, showSnackbar, loadData]);

  // Delete template
  const deleteTemplate = useCallback(async (templateId: number) => {
    try {
      await apiService.deletePdfTemplate(templateId);
      showSnackbar('Template deleted successfully');
      loadData();
    } catch (err) {
      console.error('Error deleting template:', err);
      showSnackbar('Failed to delete template', 'error');
    }
  }, [showSnackbar, loadData]);

  // Open add dialog
  const openAddDialog = useCallback(() => {
    // Keep the document selection if it exists
    // Only set a default template type if permissions are available
    if (permissions.length > 0 && permissions.some(p => p.isActive)) {
      const defaultPermission = permissions.find(p => p.isActive);
      setSelectedTemplateType(defaultPermission?.permissionName || '');
    } else {
      setSelectedTemplateType('');
    }
    setAddDialogOpen(true);
  }, [permissions]);

  // Parse extracted fields from string or array
  const parseExtractedFields = useCallback((fields: string | string[] | undefined): string[] => {
    if (!fields) return [];
    if (Array.isArray(fields)) return fields;
    return fields.split(',').filter(Boolean).map(f => f.trim());
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={loadData}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box>
      <SummaryCard 
        templates={templates} 
        openAddDialog={openAddDialog} 
      />

      {/* Template Groups */}
      {Object.keys(groupedTemplates).length > 0 ? (
        Object.entries(groupedTemplates).map(([type, templateList]) => (
          <TemplateGroup
            key={type}
            type={type}
            templates={templateList}
            documents={documents}
            parseExtractedFields={parseExtractedFields}
            extractFields={extractFields}
            deleteTemplate={deleteTemplate}
            showSnackbar={showSnackbar}
            extractingFields={extractingFields}
          />
        ))
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No PDF templates have been created yet. Click the "Add Template" button to create one.
        </Alert>
      )}

      <AddTemplateDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        selectedDocumentId={selectedDocumentId}
        setSelectedDocumentId={setSelectedDocumentId}
        selectedTemplateType={selectedTemplateType}
        setSelectedTemplateType={setSelectedTemplateType}
        saveTemplate={saveTemplate}
        saving={saving}
        permissions={permissions}
        documents={documents}
      />

      <FieldExtractionDialog
        open={fieldExtractionDialogOpen}
        onClose={() => setFieldExtractionDialogOpen(false)}
        extractingFields={extractingFields}
        extractedFields={extractedFields}
        templateId={currentTemplateId}
        showSnackbar={showSnackbar}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShareManagement;
